import React from 'react';
import clsx from 'clsx';

import {
	CircularProgress,
	TextField as MuiTextField,
	type TextFieldProps as MuiTextFieldProps,
} from '@mui/material';
import {composeClasses, styled} from '@netx/core-components/Theme';

import textFieldClasses, {
	getStyledTextFieldUtilityClass,
} from './styledTextFieldClasses';
import {outlinedInputClasses} from '@mui/material/OutlinedInput';

const useUtilityClasses = (ownerState: any) => {
	const {classes, inherit, multiline} = ownerState;
	const slots = {
		root: ['root', multiline && 'textarea', inherit && 'inherit'],
		loading: ['loading'],
	};
	return composeClasses(
		slots,
		getStyledTextFieldUtilityClass,
		classes as typeof textFieldClasses,
	);
};

const StyledTextFieldRoot = styled(MuiTextField, {
	name: 'StyledTextField',
	slot: 'Root',
	overridesResolver: (props, styles) => styles.root,
})(({theme}) => ({
	[`&.${textFieldClasses.textarea} textarea`]: {
		minHeight: 42,
	},
	[`&.${textFieldClasses.inherit}`]: {
		color: 'inherit',
		[`& .${outlinedInputClasses.root}`]: {
			background: `rgba(0, 0, 0, 0.16)`,
			paddingRight: 8,
			color: 'inherit',
			[`&.${outlinedInputClasses.focused}`]: {
				[`& .${outlinedInputClasses.notchedOutline}`]: {
					borderColor: `rgba(0, 0, 0, 0.32)`,
				},
			},
			[`&:not(.${outlinedInputClasses.focused})`]: {
				[`& .${outlinedInputClasses.notchedOutline}`]: {
					borderColor: 'transparent',
				},
			},
			[`& .${outlinedInputClasses.input}`]: {
				paddingLeft: 8,
				textAlign: 'center',
				color: 'inherit',
			},
		},
	},
}));

const LoadingRoot = styled(CircularProgress, {
	name: 'StyledTextField',
	slot: 'Loading',
	overridesResolver: (props, styles) => styles.loading,
})(({theme}) => ({
	position: 'absolute',
	right: 10,
	top: 10,
	'& circle': {
		strokeWidth: 2,
	},
}));

export type StyledTextFieldProps = MuiTextFieldProps & {
	// TODO: this currently only supports outlined inputs
	inherit?: boolean;
	loadingValue?: boolean;
	readOnly?: boolean;
};

export const StyledTextFieldPrimitive = (
	props: StyledTextFieldProps,
	ref: any,
) => {
	const {
		className,
		autoComplete,
		loadingValue,
		disabled,
		inherit,
		readOnly,
		...sendProps
	} = props;

	const classes = useUtilityClasses({
		...sendProps,
		inherit,
	});

	return (
		<StyledTextFieldRoot
			ref={ref}
			data-testid="form-input-text-field"
			className={clsx(classes.root, className)}
			disabled={disabled || loadingValue}
			fullWidth
			{...sendProps}
			InputProps={{
				...sendProps.InputProps,
				endAdornment: (
					<>
						{loadingValue ? (
							<LoadingRoot
								size={20}
								className={classes.loading}
								variant="indeterminate"
							/>
						) : null}
						{sendProps.InputProps?.endAdornment}
					</>
				),
			}}
			inputProps={{
				autoComplete: autoComplete || 'off',
				readOnly,
				...sendProps.inputProps,
			}}
		/>
	);
};

export const StyledTextField = React.memo(
	React.forwardRef(StyledTextFieldPrimitive),
);
export default StyledTextField;
